import { ProductCategory } from '@api/types';
import { CategorySelectorSubitem } from '@components';
import { Add, Remove } from '@mui/icons-material';
import { Box, Collapse, styled } from '@mui/material';
import { colors } from '@themes/colors';
import { breakpoints } from '@themes/theme.helpers';
import classNames from 'classnames';
import { useEffect, useState } from 'react';

// STYLES
//--------
const CategorySelectorItemWrapper = styled(Box)``;

const CategoryBox = styled(Box)`
  display: flex;
  font-weight: 400;
  background-color: white;
  margin-bottom: 10px;
  padding: 6px 16px 6px 10px;
  gap: 8px;
  color: ${colors.background.black};
  border-radius: 4px;
  &.active {
    font-weight: 700;
  }
`;

export const CategoryName = styled(Box)`
  cursor: pointer;
`;

export const ProductCategoryCount = styled(Box)`
  color: ${colors.primary.darkGreen};
  margin-left: auto;
  &.subproduct-count {
    font-weight: 400;
    margin-left: 8px;
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 160%;
    color: #616a6e;
    letter-spacing: 0.01em;
  }
`;

const IconBox = styled(Box)`
  ${breakpoints.up('md')} {
    width: 12px;
  }
`;

const StyledAdd = styled(Add)`
  color: #aaa;
  font-size: 12px;
  cursor: pointer;
`;

const StyledRemove = styled(Remove)`
  color: #aaa;
  font-size: 12px;
  cursor: pointer;
`;

// TYPES
//-------

export type CategorySelectorItemProps = {
  category?: ProductCategory;
  skipEmptyCategories?: boolean;
  onChange: (category: ProductCategory) => void;
  isCategorySelected: (category: ProductCategory) => boolean;
  isCategoryOpened: (category: ProductCategory) => boolean;
  shouldClose?: boolean;
};

// COMPONENTS
//------------
export const CategorySelectorItem = ({
  onChange,
  category = {},
  skipEmptyCategories,
  isCategorySelected,
  isCategoryOpened,
  shouldClose,
}: CategorySelectorItemProps) => {
  const isOpened = isCategoryOpened(category);
  const [opened, setOpened] = useState<boolean>(isOpened);
  const openItem = () => setOpened(true);
  const closeItem = () => setOpened(false);

  const isEmpty = !category?.productCount || category?.productCount === 0;

  useEffect(() => {
    if (typeof shouldClose !== 'undefined') {
      if (shouldClose) {
        closeItem();
        return;
      }
      openItem();
    }
  }, [shouldClose]);

  if (skipEmptyCategories && isEmpty) {
    return null;
  }
  const boxClass = isCategorySelected(category) ? 'active' : '';
  const changeHandler = () => {
    openItem();
    onChange(category);
  };

  // Expand / collapse
  const hasNestedCategories = Boolean(
    category?.nestedCategories?.length &&
      category?.nestedCategories?.length > 0 &&
      category?.nestedCategories?.some((nestedCategory) => (nestedCategory?.productCount ?? 0) > 0)
  );

  return (
    <CategorySelectorItemWrapper>
      <CategoryBox className={classNames(boxClass)}>
        <IconBox>
          {opened && hasNestedCategories && <StyledRemove onClick={closeItem} />}
          {!opened && hasNestedCategories && <StyledAdd onClick={openItem} />}
        </IconBox>
        <CategoryName onClick={changeHandler}>{category?.displayName}</CategoryName>
        <ProductCategoryCount>{category?.productCount}</ProductCategoryCount>
      </CategoryBox>
      <Collapse in={opened}>
        {category?.nestedCategories?.map((nestedCategory, key) => (
          <CategorySelectorSubitem
            onChange={onChange}
            category={nestedCategory}
            key={key}
            skipEmptyCategories={skipEmptyCategories}
            isCategorySelected={isCategorySelected}
          />
        ))}
      </Collapse>
    </CategorySelectorItemWrapper>
  );
};
